import React, { useState, useEffect, useCallback } from 'react';
import { API_ADDRESS, PAGE_TITLE_BASE } from './constants.js';
import { useNavigate } from "react-router";
import { Dimmer, Loader, Segment, Dropdown } from 'semantic-ui-react'
import useUrlState from '@ahooksjs/use-url-state';
import { ConcertCard } from "./ConcertCard";
import { DateRangePicker } from "react-date-range";
import { createStaticRanges } from "react-date-range";
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { addDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, endOfYear, isSameDay, differenceInCalendarDays } from 'date-fns';
import { enGB } from 'date-fns/locale';
import _ from 'lodash'
import { ConcertMap } from './ConcertMap';

export function Concerts(props) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [concerts, setConcerts] = useState({});
  const [filteredConcerts, setFilteredConcerts] = useState({});
  const [markers, setMarkers] = useState([]);
  const [filteredMarkers, setFilteredMarkers] = useState([]);
  const [artistOptions, setArtistOptions] = useState([]);
  const [venueOptions, setVenueOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [festivalOptions, setFestivalOptions] = useState([]);
  const [showOptions, setShowOptions] = useState([]);
  const [actOptions, setActOptions] = useState([]);
  const [accompanimentOptions, setAccompanimentOptions] = useState([]);

  let stateTmp = {
    start_date: new Date(),
    end_date: addDays(new Date(), 27),
    artist: [],
    venue: [],
    city: [],
    festival: [],
    show: [],
    accompaniment: [],
    act: []
  };
  const [urlState] = useUrlState(stateTmp,
    {
      parseOptions: {
        arrayFormat: 'comma',
        skipEmptyString: true
      },
      stringifyOptions: {
        arrayFormat: 'comma',
      },
    }
  );

  const [dateState, setDateState] = useState([{
    startDate: urlState.start_date instanceof Date ? urlState.start_date : new Date(Date.parse(urlState.start_date)),
    endDate: urlState.end_date instanceof Date ? urlState.end_date : new Date(Date.parse(urlState.end_date)),
    key: 'selection'    
  }]);
  const [filters, setFilters] = useState({
    artist: Array.isArray(urlState.artist) ? urlState.artist : urlState.artist !== '' ? [urlState.artist] : [],
    venue: Array.isArray(urlState.venue) ? urlState.venue : urlState.venue !== '' ? [urlState.venue] : [],
    city: Array.isArray(urlState.city) ? urlState.city : urlState.city !== '' ? [urlState.city] : [],
    festival: Array.isArray(urlState.festival) ? urlState.festival : urlState.festival !== '' ? [urlState.festival] : [],
    show: Array.isArray(urlState.show) ? urlState.show : urlState.show !== '' ? [urlState.show] : [],
    accompaniment: Array.isArray(urlState.accompaniment) ? urlState.accompaniment : urlState.accompaniment !== '' ? [urlState.accompaniment] : [],
    act: Array.isArray(urlState.act) ? urlState.act : urlState.act !== '' ? [urlState.act] : [],
  });

  document.title = 'Concerts | ' + PAGE_TITLE_BASE;

  const getUrlAttachment = useCallback(() => {
    let start_date = undefined;
    let end_date = undefined;
    try {
      start_date = dateState[0].startDate.getFullYear() + '-' + String(dateState[0].startDate.getMonth()+1).padStart(2, '0') +  '-' + String(dateState[0].startDate.getDate()).padStart(2, '0');
      end_date = dateState[0].endDate.getFullYear() + '-' + String(dateState[0].endDate.getMonth()+1).padStart(2, '0') +  '-' + String(dateState[0].endDate.getDate()).padStart(2, '0');
    } catch (error) {
      start_date = new Date();
      start_date = start_date.getFullYear() + '-' + String(start_date.getMonth()).padStart(2, '0') +  '-' + String(start_date.getDate()).padStart(2, '0')
      end_date = addDays(new Date(), 27);
      end_date = end_date.getFullYear() + '-' + String(end_date.getMonth()).padStart(2, '0') +  '-' + String(end_date.getDate()).padStart(2, '0');
    } 
    let urlAttachment = `?start_date=${start_date}&end_date=${end_date}`;

    if(filters.artist) {
      urlAttachment += '&artist=' + filters.artist.join(',');
    }
    if(filters.venue) {
      urlAttachment += '&venue=' + filters.venue.join(',');
    }
    if(filters.city) {
      urlAttachment += '&city=' + filters.city.join(',');
    }
    if(filters.festival) {
      urlAttachment += '&festival=' + filters.festival.join(',');
    }
    if(filters.show) {
      urlAttachment += '&show=' + filters.show.join(',');
    }
    if(filters.accompaniment) {
      urlAttachment += '&accompaniment=' + filters.accompaniment.join(',');
    }
    if(filters.act) {
      urlAttachment += '&act=' + filters.act.join(',');
    }

    return urlAttachment;
  }, [dateState, filters]);

  const search = useCallback(() => {
    if(dateState[0].startDate && dateState[0].endDate) {
      let urlAttachment = getUrlAttachment();
      let url = `${API_ADDRESS}/concerts${urlAttachment}`;
      navigate(`/concert${urlAttachment}`);

      fetch(url)
        .then(function(response) {
          return response.json()
        })
        .then(function(json) {
          if(!json['error']) {
            setConcerts(json['concerts']);
            setMarkers(json['markers']);
            setArtistOptions(_.map(Object.entries(json['artists']).sort((a, b) => a[1].localeCompare(b[1])), (artist, i) => ({
              key: artist[0],
              text: artist[1],
              value: artist[0]
            })));
            setVenueOptions(_.map(Object.entries(json['venues']).sort((a, b) => a[1].localeCompare(b[1])), (venue, i) => ({
              key: venue[0],
              text: venue[1],
              value: venue[0]
            })));
            setCityOptions(_.map(Object.entries(json['cities']).sort((a, b) => a[1].localeCompare(b[1])), (city, i) => ({
              key: city[0],
              text: city[1],
              value: city[0]
            })));
            setFestivalOptions(_.map(Object.entries(json['festivals']).sort((a, b) => a[1].localeCompare(b[1])), (festival, i) => ({
              key: festival[0],
              text: festival[1],
              value: festival[0]
            })));
            setShowOptions(_.map(Object.entries(json['shows']).sort((a, b) => a[1].localeCompare(b[1])), (show, i) => ({
              key: show[0],
              text: show[1],
              value: show[0]
            })));
            setAccompanimentOptions(_.map(Object.entries(json['accompaniments']).sort((a, b) => a[1].localeCompare(b[1])), (accompaniment, i) => ({
              key: accompaniment[0],
              text: accompaniment[1],
              value: accompaniment[0]
            })));
            setActOptions(_.map(Object.entries(json['acts']).sort((a, b) => a[1].localeCompare(b[1])), (act, i) => ({
              key: act[0],
              text: act[1],
              value: act[0]
            })));
          }
          setLoading(false);
        })
        .catch(function(ex) {
          setLoading(false);
          console.log(ex);
        })
    }
  }, [dateState, getUrlAttachment, navigate]); 

  useEffect(() => { if(loading) { search() } }, [loading, search])

  function handleDateChange(item) {
    setDateState([item.selection])
    setLoading(true);
  }

  const filterHelper = useCallback((filtersTmp) => {
    let filtersToSet = {};
    for(const key in filtersTmp) {
      if(filtersTmp[key] !== undefined && filters[key] !== filtersTmp[key]) {
        filtersToSet[key] = filtersTmp[key];
      }
    }
    if(Object.keys(filtersToSet).length > 0) {
      setFilters(prevFilters => ({...prevFilters, ...filtersToSet}));
    }
  }, [filters, setFilters]);

  useEffect(() => {
    let filteredConcerts = {};
    let filteredMarkers = {};
    let filteredConcertIds = [];
    if(concerts) {
    Object.keys(concerts).forEach(function(date) {
      let filteredConcertList = [];
      concerts[date].forEach(function(concert) {
        let include = true;
        if(filters.artist.length > 0) {
          include = false;
          if(concert['artists']) {
            concert['artists'].forEach(function(artist) {
              if(filters.artist.includes(String(artist.artist_id))) {
                include = true;
              }
            });
          }
        }
        if(include && filters.venue.length > 0) {
         include = false;
          if(filters.venue.includes(String(concert.location_id))) {
            include = true;
          }
        }
        if(include && filters.city.length > 0) {
         include = false;
          if(filters.city.includes(String(concert.location.city))) {
            include = true;
          }
        }
        if(include && filters.festival.length > 0) {
         include = false;
          if(filters.festival.includes(String(concert.festival_id))) {
            include = true;
          }
        }
        if(include && filters.show.length > 0) {
         include = false;
          if(filters.show.includes(String(concert.show_id))) {
            include = true;
          }
        }
        if(include && filters.accompaniment.length > 0) {
         include = false;
          if(concert['accompaniments']) {
            concert['accompaniments'].forEach(function(accompaniment) {
              if(filters.accompaniment.includes(String(accompaniment.accompaniment_id))) {
                include = true;
              }
            });
          }
        }
        if(include && filters.act.length > 0) {
         include = false;
          if(filters.act.includes(String(concert.act_id)) || filters.act.includes(String(concert.act2_id))) {
            include = true;
          }
        }
        if(include) {
          filteredConcertList.push(concert);
          filteredConcertIds.push(concert.concert_id);
          filteredMarkers[concert.location_id] = Object.assign({}, markers[concert.location_id]);
        }
      });

      if(filteredConcertList.length > 0) {
        filteredConcerts[date] = filteredConcertList;
      }
    });
    }
    setFilteredConcerts(filteredConcerts);
    Object.entries(filteredMarkers).map(function(marker) {
      let newConcerts = [];
      marker[1]['concerts'].map(function(concert) {
        if(filteredConcertIds.includes(concert.concert_id)) {
          newConcerts.push(concert);
        }
        return concert;
      });
      marker[1]['concerts'] = newConcerts;
      return newConcerts;
    });
    setFilteredMarkers(filteredMarkers);

    let urlAttachment = getUrlAttachment();
    navigate(`/concert${urlAttachment}`);
  }, [concerts, filters, getUrlAttachment, markers, navigate]);

  return (
    <div>
      <Segment className="browse-table-segment">
        <Dimmer active={loading}>
          <Loader size='large'>Loading</Loader>
        </Dimmer>
        <h1>Concert Calendar</h1>
        <div className="Cards">
          {Object.keys(filteredConcerts).length > 0 && Object.keys(filteredConcerts).sort().map(function(date) {
            let concerts_for_date = filteredConcerts[date];
            return <ConcertCard key={date} date={date} concerts={concerts_for_date}/>
          })}
          {Object.keys(filteredConcerts).length === 0 &&
            <b>No results</b>
          }
        </div>
        {(filteredMarkers && Object.keys(filteredMarkers).length > 0) &&
          <ConcertMap markers={filteredMarkers}/>
        }
        <div className="Filters">
          <h2>Filters</h2>
          <div className="FiltersRow">
            <div className="DropdownFilter">
              <h3>Artist</h3>
              <Dropdown
                placeholder='Filter by Artist'
                fluid
                multiple
                search
                selection
                options={artistOptions}
                value={filters.artist}
                onChange={(e, { value }) => {filterHelper({"artist": value});}}
              />
            </div>
            <div className="DropdownFilter">
              <h3>Act</h3>
              <Dropdown
                placeholder='Filter by Act'
                fluid
                multiple
                search
                selection
                options={actOptions}
                value={filters.act}
                onChange={(e, { value }) => {filterHelper({"act": value});}}
              />
            </div>
            <div className="DropdownFilter">
              <h3>Venue</h3>
              <Dropdown
                placeholder='Filter by Venue'
                fluid
                multiple
                search
                selection
                options={venueOptions}
                value={filters.venue}
                onChange={(e, { value }) => {filterHelper({"venue": value});}}
              />
            </div>
            <div className="DropdownFilter">
              <h3>City</h3>
              <Dropdown
                placeholder='Filter by City'
                fluid
                multiple
                search
                selection
                options={cityOptions}
                value={filters.city}
                onChange={(e, { value }) => {filterHelper({"city": value});}}
              />
            </div>
            <div className="DropdownFilter">
              <h3>Show</h3>
              <Dropdown
                placeholder='Filter by Show'
                fluid
                multiple
                search
                selection
                options={showOptions}
                value={filters.show}
                onChange={(e, { value }) => {filterHelper({"show": value});}}
              />
            </div>
            <div className="DropdownFilter">
              <h3>Festival / Club</h3>
              <Dropdown
                placeholder='Filter by Festival'
                fluid
                multiple
                search
                selection
                options={festivalOptions}
                value={filters.festival}
                onChange={(e, { value }) => {filterHelper({"festival": value});}}
              />
            </div>
            <div className="DropdownFilter">
              <h3>Accompaniment</h3>
              <Dropdown
                placeholder='Filter by Accompaniment'
                fluid
                multiple
                search
                selection
                options={accompanimentOptions}
                value={filters.accompaniment}
                onChange={(e, { value }) => {filterHelper({"accompaniment": value});}}
              />
            </div>
          </div>
          <h3>Date</h3>
          <DateRangePicker
            onChange={handleDateChange}
            months={1}
            ranges={dateState}
            scroll={{ enabled: true }}
            direction="vertical"
            minDate={new Date('2026-01-01')}
            maxDate={addDays(new Date(), 600)}
            locale={enGB}
            staticRanges={createStaticRanges([
              {
                label: 'Today',
                range: () => ({
                  startDate: startOfDay(new Date()),
                  endDate: endOfDay(new Date()),
                }),
              },
              {
                label: 'This Week',
                range: () => ({
                  startDate: startOfWeek(new Date()),
                  endDate: endOfWeek(new Date()),
                }),
              },
              {
                label: 'This Month',
                range: () => ({
                  startDate: startOfMonth(new Date()),
                  endDate: endOfMonth(new Date()),
                }),
              },
              {
                label: 'This Year',
                range: () => ({
                  startDate: startOfMonth(new Date()),
                  endDate: endOfYear(new Date()),
                }),
              },
              {
                label: 'All Future Shows',
                range: () => ({
                  startDate: new Date(),
                  endDate: addDays(new Date(), 600),
                }),
              },
              {
                label: 'All Shows (including Past)',
                range: () => ({
                  startDate: startOfMonth(new Date('2026-01-01')),
                  endDate: addDays(new Date(), 600),
                }),
              },
            ])}
            inputRanges={[
              {
                label: 'days starting today',
                range(value) {
                  const today = new Date();
                  return {
                    startDate: today,
                    endDate: addDays(today, Math.max(Number(value), 1) - 1),
                  };
                },
                getCurrentValue(range) {
                  if (!isSameDay(range.startDate, startOfDay(new Date()))) return '-';
                  if (!range.endDate) return '∞';
                  return differenceInCalendarDays(range.endDate, startOfDay(new Date())) + 1;
                },
              },
            ]}
          />
          </div>
      </Segment>
    </div>
  );
};
